// Durable local storage — single public interface used by the whole offline layer.
// Selects the right adapter per platform WITHOUT changing any caller:
//   • web / PWA / Capacitor WebView  → IndexedDB (default, always available)
//   • native (Capacitor iOS/Android) → SQLite (durable + device-backup safe)
// The SQLite module is dynamically imported ONLY on native, so the web bundle
// never eagerly loads the native plugin.
//
// SAFETY (statutory records): on native, SQLite is REQUIRED. If it fails to
// initialise we DO NOT silently fall back to IndexedDB — falling back would give a
// false sense of durable capture. Instead every storage call throws a typed
// OfflineStorageUnavailableError carrying a support reference, and a diagnostic
// event is emitted. Callers must surface a clear message and must NOT confirm a save.
import { Capacitor } from "@capacitor/core";
import { createIndexedDbStorage, DB_VERSION } from "./storageIndexedDb";

let _implPromise = null;

export class OfflineStorageUnavailableError extends Error {
  constructor(reference, cause) {
    super("offline_storage_unavailable");
    this.name = "OfflineStorageUnavailableError";
    this.reference = reference;
    this.cause = cause;
    this.userMessage =
      "This device can’t save records offline right now, so your entry was NOT saved. " +
      "Please reconnect to the internet or contact support with the reference below.";
  }
}

function isNative() {
  try {
    return typeof Capacitor?.isNativePlatform === "function" && Capacitor.isNativePlatform();
  } catch (e) {
    return false;
  }
}

function newReference() {
  return `OFS-${Date.now().toString(36)}-${Math.random().toString(36).slice(2, 6)}`.toUpperCase();
}

function diag(name, reference, cause) {
  try {
    const evt = { name, reference, at: Date.now(), detail: cause && (cause.message || String(cause)) };
    if (typeof window !== "undefined") {
      window.__cc_offline_events = window.__cc_offline_events || [];
      window.__cc_offline_events.push(evt);
      // Surface to any mounted UI so the user gets a clear NOT-SAVED message (no raw internals).
      try { window.dispatchEvent(new CustomEvent("cc:offline-storage-error", { detail: { reference } })); } catch (e) { /* noop */ }
    }
    // eslint-disable-next-line no-console
    console.error(`[offline] ${name} ref=${reference}`, cause);
  } catch (e) { /* noop */ }
}

async function build() {
  if (!isNative()) return createIndexedDbStorage();
  // Native platform: SQLite is mandatory for durable statutory capture.
  try {
    const { createSqliteStorage } = await import("./storageSqlite");
    const candidate = createSqliteStorage();
    await candidate.all("meta"); // probe forces open + migrations so failures surface now
    return candidate;
  } catch (e) {
    // NEVER fall back to IndexedDB on native — surface a safe, actionable failure.
    const reference = newReference();
    diag("offline_native_storage_init_failed", reference, e);
    throw new OfflineStorageUnavailableError(reference, e);
  }
}

// Single shared init promise so concurrent callers collapse onto ONE initialisation
// (prevents duplicate native connection attempts). On failure the promise is cleared so a
// later call can retry and recover from a transient native failure; a successful adapter
// is cached for the session.
function impl() {
  if (!_implPromise) {
    _implPromise = build().catch((e) => { _implPromise = null; throw e; });
  }
  return _implPromise;
}

export const storage = {
  async put(store, key, value) {
    return (await impl()).put(store, key, value);
  },
  async get(store, key) {
    return (await impl()).get(store, key);
  },
  async all(store) {
    return (await impl()).all(store);
  },
  async del(store, key) {
    return (await impl()).del(store, key);
  },
};

export const DB_SCHEMA_VERSION = DB_VERSION;
